import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ArrowDown, ArrowUp, Image, Trash2, Upload, X } from "lucide-react";
import { api, apiError, imgUrl } from "@/lib/api";
import ImageField from "@/components/ImageField";

const EMPTY = { name: "", description: "", cover_image: "", starting_price: "" };

export default function CategoriesTab() {
  const [categories, setCategories] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [pickerFor, setPickerFor] = useState(null);
  const [uploadFor, setUploadFor] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const uploadPhotos = async (files) => {
    if (!uploadFor) return;
    setUploading(true);
    let ok = 0;
    for (const file of files) {
      try {
        const fd = new FormData();
        fd.append("file", file);
        const up = await api.post("/admin/upload", fd);
        await api.post("/admin/photos", { url: up.data.url, categories: [uploadFor], featured: false, hidden: false });
        ok++;
      } catch (e) {
        toast.error(`${file.name}: ${apiError(e)}`);
      }
    }
    setUploading(false);
    if (ok) toast.success(`${ok} photo${ok > 1 ? "s" : ""} added — reorder them in the Portfolio tab`);
    fileRef.current.value = "";
  };

  const load = () => {
    api.get("/admin/categories").then((r) => setCategories(r.data)).catch((e) => toast.error(apiError(e)));
    api.get("/admin/photos").then((r) => setPhotos(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/categories", form);
      setForm(EMPTY);
      load();
      toast.success("Category added");
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const update = async (id, patch) => {
    try {
      const r = await api.put(`/admin/categories/${id}`, patch);
      setCategories((arr) => arr.map((c) => (c.id === id ? r.data : c)));
      toast.success("Saved");
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this category? Photos remain but lose this tag.")) return;
    await api.delete(`/admin/categories/${id}`);
    load();
  };

  const move = async (index, dir) => {
    const arr = [...categories];
    const j = index + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[index], arr[j]] = [arr[j], arr[index]];
    setCategories(arr);
    await api.put("/admin/categories/reorder", { ordered_ids: arr.map((c) => c.id) });
  };

  const inputCls = "border border-[#E5E5E2] px-3 py-2 text-sm outline-none focus:border-[#1A1A1A] w-full";

  return (
    <div data-testid="admin-categories">
      <form onSubmit={add} className="bg-white border border-[#E5E5E2] p-6 mb-8 grid sm:grid-cols-2 gap-4">
        <input required placeholder="Category name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
          data-testid="category-add-name" className={inputCls} />
        <input placeholder="Starting price (e.g. From $350)" value={form.starting_price} onChange={(e) => setForm({ ...form, starting_price: e.target.value })}
          data-testid="category-add-price" className={inputCls} />
        <textarea placeholder="Description — Enter for a new line, two Enters for a new paragraph" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
          rows={3} data-testid="category-add-description" className={`${inputCls} resize-y sm:col-span-2`} />
        <div className="sm:col-span-2">
          <p className="text-[10px] uppercase tracking-[0.15em] text-[#7A7A7A] mb-1.5">Cover photo</p>
          <ImageField value={form.cover_image} onChange={(url) => setForm({ ...form, cover_image: url })} testId="category-add-cover" />
        </div>
        <button type="submit" data-testid="category-add-submit"
          className="sm:col-span-2 bg-[#1A1A1A] text-white text-xs uppercase tracking-[0.15em] py-3 hover:bg-[#4A4A4A] transition-colors">
          Add Category
        </button>
      </form>

      <div className="space-y-3">
        <input ref={fileRef} type="file" accept="image/*" multiple hidden data-testid="category-bulk-upload"
          onChange={(e) => uploadPhotos([...e.target.files])} />
        {uploading && <p className="text-sm text-[#7A7A7A] bg-white border border-[#E5E5E2] p-3">Uploading photos…</p>}
        {categories.map((c, i) => (
          <div key={c.id} data-testid={`admin-category-${c.id}`} className="bg-white border border-[#E5E5E2] p-4">
            <div className="flex flex-wrap lg:flex-nowrap gap-4 items-center">
              {c.cover_image && <img src={imgUrl(c.cover_image)} alt="" className="w-16 h-16 object-cover" />}
              <div className="grid sm:grid-cols-3 gap-2 flex-1">
                <input defaultValue={c.name} data-testid={`category-name-${c.id}`} className={inputCls}
                  onBlur={(e) => e.target.value !== c.name && update(c.id, { name: e.target.value })} />
                <input defaultValue={c.starting_price} placeholder="Starting price" data-testid={`category-price-${c.id}`} className={inputCls}
                  onBlur={(e) => e.target.value !== c.starting_price && update(c.id, { starting_price: e.target.value })} />
                <div>
                  <p className="text-[10px] uppercase tracking-[0.15em] text-[#7A7A7A] mb-1">Cover</p>
                  <ImageField compact value={c.cover_image} onChange={(url) => update(c.id, { cover_image: url })} testId={`category-cover-${c.id}`} />
                </div>
                <textarea defaultValue={c.description} placeholder="Description — Enter for a new line, two Enters for a new paragraph" rows={3}
                  data-testid={`category-desc-${c.id}`} className={`${inputCls} resize-y sm:col-span-3`}
                  onBlur={(e) => e.target.value !== c.description && update(c.id, { description: e.target.value })} />
              </div>
              <div className="flex gap-1 items-center">
                <label className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.1em] text-[#7A7A7A] border border-[#E5E5E2] px-2 py-1.5 cursor-pointer" title="Show on Pricing page">
                  <input type="checkbox" checked={c.show_in_pricing !== false}
                    onChange={(e) => update(c.id, { show_in_pricing: e.target.checked })}
                    data-testid={`category-pricing-${c.id}`} className="accent-[#1A1A1A] w-3.5 h-3.5" />
                  Pricing
                </label>
                <button onClick={() => { setUploadFor(c.slug); fileRef.current?.click(); }} data-testid={`category-upload-${c.id}`}
                  title="Upload photos to this category"
                  className="p-2 border border-[#E5E5E2]" disabled={uploading}>
                  <Upload size={13} />
                </button>
                <button onClick={() => setPickerFor(pickerFor === c.id ? null : c.id)} data-testid={`category-pick-cover-${c.id}`}
                  title="Pick cover from portfolio"
                  className={`p-2 border ${pickerFor === c.id ? "border-[#1A1A1A] bg-[#1A1A1A] text-white" : "border-[#E5E5E2]"}`}>
                  <Image size={13} />
                </button>
                <button onClick={() => move(i, -1)} data-testid={`category-up-${c.id}`} className="p-2 border border-[#E5E5E2]"><ArrowUp size={13} /></button>
                <button onClick={() => move(i, 1)} data-testid={`category-down-${c.id}`} className="p-2 border border-[#E5E5E2]"><ArrowDown size={13} /></button>
                <button onClick={() => remove(c.id)} data-testid={`category-delete-${c.id}`} className="p-2 border border-[#E5E5E2] text-red-500"><Trash2 size={13} /></button>
              </div>
            </div>
            {pickerFor === c.id && (
              <div data-testid={`cover-picker-${c.id}`} className="mt-4 border-t border-[#E5E5E2] pt-4">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-[10px] uppercase tracking-[0.2em] text-[#7A7A7A]">Choose a cover from your portfolio</p>
                  <button onClick={() => setPickerFor(null)} className="text-[#7A7A7A]"><X size={14} /></button>
                </div>
                <div className="grid grid-cols-4 sm:grid-cols-6 lg:grid-cols-8 gap-2 max-h-56 overflow-y-auto">
                  {photos.map((p) => (
                    <button key={p.id} onClick={() => { update(c.id, { cover_image: p.url }); setPickerFor(null); }}
                      data-testid={`cover-option-${p.id}`}
                      className={`aspect-square overflow-hidden border-2 ${c.cover_image === p.url ? "border-[#1A1A1A]" : "border-transparent hover:border-[#7A7A7A]"}`}>
                      <img src={imgUrl(p.url)} alt={p.caption || ""} loading="lazy" className="w-full h-full object-cover" />
                    </button>
                  ))}
                  {photos.length === 0 && <p className="text-xs text-[#7A7A7A] col-span-full">No photos yet — upload some in the Portfolio tab first.</p>}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
